import React, { useImperativeHandle, forwardRef, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { NotificationsProps, NotificationOptions } from "./types/NotificationTypes";

const DEFAULT_OPTIONS: Partial<NotificationOptions> = {
  autoDismiss: 5,
  position: "tr",
};

const verticalMap = {
  t: "top" as const,
  b: "bottom" as const,
};

const horizontalMap = {
  r: "right" as const,
  l: "left" as const,
  c: "center" as const,
};

export interface NotificationsRef {
  addNotification: (notification: NotificationOptions) => void;
}

export const Notifications = forwardRef<NotificationsRef, NotificationsProps>(
  ({ defaultOptions = DEFAULT_OPTIONS }, ref) => {
    const [open, setOpen] = useState(false);
    const [notification, setNotification] = useState<NotificationOptions | null>(null);

    useImperativeHandle(ref, () => ({
      addNotification: (newNotification: NotificationOptions) => {
        setNotification({
          ...defaultOptions,
          ...newNotification,
        });
        setOpen(true);
      },
    }));

    const handleClose = () => {
      setOpen(false);
    };

    const getPosition = () => {
      const pos = notification?.position || defaultOptions.position || "tr";
      const [v, h] = pos.split("");
      return {
        vertical: verticalMap[v as keyof typeof verticalMap],
        horizontal: horizontalMap[h as keyof typeof horizontalMap],
      };
    };

    if (!notification) return null;

    const { vertical, horizontal } = getPosition();

    return (
      <Snackbar
        open={open}
        autoHideDuration={notification.autoDismiss ? notification.autoDismiss * 1000 : undefined}
        onClose={handleClose}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert
          onClose={notification.dismissible === false ? undefined : handleClose}
          severity={notification.level}
          sx={{ width: '100%' }}
        >
          {notification.title && <strong>{notification.title}<br /></strong>}
          {notification.message}
        </Alert>
      </Snackbar>
    );
  }
); 